'use client';
import Link from '@/components/ui/Link';

interface ProductProps {
    image: string;
    name: string;
    price: string;
    rating: number;
    reviews: number;
    imgClass?: string;
}

const ProductCard = ({ image, name, price, rating, reviews, imgClass = 'object-cover object-[center_top]' }: ProductProps) => (
    <div className="flex flex-col gap-2 sm:gap-3 group">
        {/* Edge-to-Edge Image Container with uncropped top alignment */}
        <div className="relative overflow-hidden rounded-2xl bg-[#f4f4f6] aspect-[4/5] w-full flex items-center justify-center">
            <img
                src={image}
                alt={name}
                loading="lazy"
                decoding="async"
                className={`w-full h-full ${imgClass}`}
            />
        </div>
        <div className="flex flex-col gap-1.5 sm:gap-2">
            <div className="flex flex-col gap-1 text-center">
                <h3 className="text-base sm:text-lg lg:text-xl text-text-primary font-semibold leading-tight line-clamp-1 group-hover:text-primary-brand transition-colors">
                    {name}
                </h3>
                <p className="text-base sm:text-lg text-primary-accent font-bold leading-tight">
                    {price}
                </p>
            </div>

            {/* Rating */}
            <div className="flex flex-row items-center gap-1 justify-center">
                {[...Array(5)].map((_, index) => (
                    <img
                        key={index}
                        src="/images/img_frame_amber_500.svg"
                        alt=""
                        className="w-3 h-3 sm:w-3.5 sm:h-3.5"
                    />
                ))}
                <span className="text-xs text-text-muted font-medium ml-1">
                    ({reviews})
                </span>
            </div>
        </div>
    </div>
)

export default function NewArrivalsSection() {
    const products: ProductProps[] = [
        {
            image: '/images/img_image.webp',
            name: 'Yves Saint Classic',
            price: '₹39.99',
            rating: 5,
            reviews: 128,
            imgClass: 'object-cover object-[center_top]'
        },
        {
            image: '/images/img_image_382x336.webp',
            name: 'Yves Saint Sunglasses',
            price: '₹39.99',
            rating: 5,
            reviews: 128,
            imgClass: 'object-cover object-center'
        },
        {
            image: '/images/img_image_4.webp',
            name: 'Yves Saint Summer',
            price: '₹39.99',
            rating: 5,
            reviews: 128,
            imgClass: 'object-cover object-[center_top]'
        },
        {
            image: '/images/img_image_5.webp',
            name: 'Yves Saint Bag',
            price: '₹39.99',
            rating: 5,
            reviews: 128,
            imgClass: 'object-cover object-center'
        },
        {
            image: '/images/img_image_6.webp',
            name: 'Yves Saint Luxe',
            price: '₹39.99',
            rating: 5,
            reviews: 128,
            imgClass: 'object-cover object-[center_top]'
        }
    ]

    return (
        <section className="w-full py-12 sm:py-16 lg:py-20">
            <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                <div className="w-full max-w-[1804px] mx-auto flex flex-col gap-6 sm:gap-8 lg:gap-[30px]">
                    {/* Section Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <h2 className="text-3xl sm:text-4xl lg:text-7xl text-text-primary font-semibold leading-tight">
                            New Arrivals
                        </h2>

                        <Link
                            href="/new-arrivals"
                            ariaLabel="View all categories"
                            className="flex flex-row items-center gap-2 sm:gap-3 lg:gap-[12px] group"
                        >
                            <span className="text-lg sm:text-xl lg:text-2xl text-text-subtle font-medium group-hover:text-primary-brand transition-colors duration-200">
                                View all categories
                            </span>
                            <img
                                src="/images/img_arrow_right_gray_600_01.svg"
                                alt=""
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-[16px] lg:h-[16px] group-hover:translate-x-1 transition-transform duration-200"
                            />
                        </Link>
                    </div>

                    {/* Products Responsive Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 lg:gap-[24px]">
                        {products.map((product, index) => (
                            <ProductCard key={index} {...product} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}